import { useCallback } from "react";

import { auditFieldNames } from "../../model/metaData";
import {
  filterObjectFields,
  filterObjectFieldsForArray,
  fiterAuditObjectFieldsForArray,
  fiterAuditUserObjectFields,
} from "../../utils/objectUtil";
import { SaveChangeResult } from "../../components/dataGridActionUtil";
import { getUserProfileFromCache } from "../../services/userSettingService";

import { InactivatableCrudDataGridProps } from "./model";
import { CrudDataGrid } from "./CrudDataGrid";

export const InactivatableCrudDataGrid = (props: InactivatableCrudDataGridProps) => {
  props.logger?.debug("Rendering InactivatableCrudDataGrid");

  const getAllData = useCallback(async (showInactive: boolean): Promise<any[]> => {
    const getUrl = showInactive
      ? `/${props.baseUrl}/get-all-with-inactive`
      : `/${props.baseUrl}/get-all`;

    if (props.isOrganizationData) {
      return await props.pageProps.appUtil.httpClients["eAccountingService"].post<any[], any>(
        getUrl,
        { organizationId: getUserProfileFromCache(props.pageProps.dataStore).defaultOrganization },
        (err) => {
          throw err;
        },
      );
    } else {
      return await props.pageProps.appUtil.httpClients["eAccountingService"].get<any[]>(
        getUrl,
        (err) => {
          throw err;
        },
      );
    }
  }, [props.baseUrl, props.isOrganizationData, props.pageProps.appUtil.httpClients, props.pageProps.dataStore]);

  const organizationDataToBeFiltered = [
    "organizationId",
    "organization",
    "createdByUser",
    "lastUpdatedByUser",
    "valueSets",
  ];

  const refreshData = useCallback(async (showInactive: boolean) => {
    const response = await getAllData(showInactive);
    if (response) {
      if (props.isOrganizationData) {
        return filterObjectFieldsForArray(response as any[], organizationDataToBeFiltered);
      } else {
        return fiterAuditObjectFieldsForArray(response as any[]);
      }
    }
    return [];
  }, [getAllData, props.isOrganizationData]);

  const filterRowDataForForm = (data: any) => filterObjectFields(data, 
      auditFieldNames.concat(props.dataToBeFilteredForForm || []));

  const buildKey = (change: { type: any; data: any; key: any }) => {
    var keyFieldName = Array.isArray(props.keyExpr) ? props.keyExpr[0] : props.keyExpr;
    return {
      [keyFieldName]: change.key,
      organizationId: getUserProfileFromCache(props.pageProps.dataStore).defaultOrganization,
    };
  };

  const sendChange = async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    switch (change.type) {
      case "insert":
        await props.beforeSave?.(change.data);
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `/${props.baseUrl}/create`,
          props.isOrganizationData
            ? {
              data: {
                ...change.data,
                organizationId: getUserProfileFromCache(props.pageProps.dataStore)
                  .defaultOrganization,
              },
            }
            : change.data,
          (err) => {
            throw err;
          },
        );
        if (response && (response as any).createdTime) {
          return {
            data: props.isOrganizationData
              ? filterObjectFields(response, organizationDataToBeFiltered)
              : fiterAuditUserObjectFields(response),
            keyExpr: props.keyExpr,
          };
        }
        break;
      case "update":
        const updateUrl = props.isOrganizationData
          ? `/${props.baseUrl}/update-fields`
          : `/${props.baseUrl}/update-fields/${change.key}`;
        const udpateData = props.isOrganizationData
          ? {
            key: buildKey(change),
            updateValues: change.data,
          }
          : change.data;
        const updateResponse = await props.pageProps.appUtil.httpClients["eAccountingService"].put(
          updateUrl,
          udpateData,
          (err) => {
            throw err;
          },
        );
        if (updateResponse && (updateResponse as any).lastUpdatedTime) {
          return {
            data: props.isOrganizationData
              ? filterObjectFields(updateResponse, organizationDataToBeFiltered)
              : fiterAuditUserObjectFields(updateResponse),
            keyExpr: props.keyExpr,
          };
        }
        break;
      case "remove":
        const deleteUrl = props.isOrganizationData
          ? `/${props.baseUrl}/delete-by-id`
          : `/${props.baseUrl}/delete/${change.key}`;
        const deleteResponse = props.isOrganizationData
          ? await props.pageProps.appUtil.httpClients["eAccountingService"].put(
            deleteUrl,
            { key: buildKey(change) },
            (err) => {
              throw err;
            },
          )
          : await props.pageProps.appUtil.httpClients["eAccountingService"].delete(
            deleteUrl,
            (err) => {
              throw err;
            },
          );
        if (deleteResponse && (deleteResponse as any).result) {
          return {
            data: "",
            keyExpr: props.keyExpr,
          };
        }
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: props.keyExpr,
    };
  };

  const { baseUrl, isOrganizationData, ...gridProps } = props;
  return (
    <CrudDataGrid
      refreshData={refreshData}
      filterRowDataForForm={filterRowDataForForm}
      sendChange={sendChange}
      {...gridProps}
    ></CrudDataGrid>
  );
};
